<template>
  <div class="glass-card p-6 rounded-xl border border-slate-700">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold text-white flex items-center gap-2">
        <i class="ph-bold ph-file text-blue-400"></i>
        用户上传文件
      </h2>
      <div class="flex gap-2">
        <div class="relative">
          <button
            type="button"
            @click="filterDropdownOpen = !filterDropdownOpen"
            class="min-w-[120px] pl-3 pr-8 py-2 bg-slate-900/90 border border-slate-700 rounded-lg text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all shadow-[0_0_0_1px_rgba(59,130,246,0.25)] flex items-center justify-between"
          >
            <span class="truncate">{{ filterResolvedLabel }}</span>
            <i class="ph-bold ph-caret-down text-slate-400 text-xs"></i>
          </button>
          <div
            v-if="filterDropdownOpen"
            class="absolute right-0 mt-2 w-40 bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl backdrop-blur p-1 z-20"
          >
            <button
              v-for="opt in filterOptions"
              :key="opt.value"
              type="button"
              @click="selectFilter(opt.value)"
              class="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white transition flex items-center justify-between"
              :class="filterResolved === opt.value ? 'bg-blue-500/10 border border-blue-500/40' : 'border border-transparent'"
            >
              <span>{{ opt.label }}</span>
              <i v-if="filterResolved === opt.value" class="ph-bold ph-check text-blue-400 text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="text-center py-8 text-slate-400">
      加载中...
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-slate-700">
            <th class="text-left py-3 px-4 text-slate-300">ID</th>
            <th class="text-left py-3 px-4 text-slate-300">用户名</th>
            <th class="text-left py-3 px-4 text-slate-300">文件名</th>
            <th class="text-left py-3 px-4 text-slate-300">路径</th>
            <th class="text-left py-3 px-4 text-slate-300">大小</th>
            <th class="text-left py-3 px-4 text-slate-300">状态</th>
            <th class="text-left py-3 px-4 text-slate-300">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in pagedFiles" :key="file.id" class="border-b border-slate-800 hover:bg-slate-800/50">
            <td class="py-3 px-4 text-slate-400">{{ file.id }}</td>
            <td class="py-3 px-4 text-white">{{ file.username || '-' }}</td>
            <td class="py-3 px-4 text-slate-300">{{ file.fileName }}</td>
            <td class="py-3 px-4 text-slate-400 text-xs">{{ file.filePath }}</td>
            <td class="py-3 px-4 text-slate-400">{{ formatFileSize(file.fileSize) }}</td>
            <td class="py-3 px-4">
              <span :class="file.resolved ? 'text-green-400' : 'text-yellow-400'">
                {{ file.resolved ? '已解决' : '未解决' }}
              </span>
            </td>
            <td class="py-3 px-4 text-left">
              <button
                @click="$emit('toggle-resolved', file.id)"
                class="px-3 py-1.5 text-xs rounded-lg border transition-all shadow-sm flex items-center gap-1 font-medium"
                :class="file.resolved
                  ? 'bg-amber-500/10 text-amber-200 border-amber-400/40 hover:bg-amber-500/20 hover:border-amber-300/60'
                  : 'bg-emerald-500/10 text-emerald-200 border-emerald-400/40 hover:bg-emerald-500/20 hover:border-emerald-300/60'"
              >
                <i :class="file.resolved ? 'ph-duotone ph-arrow-u-up-left' : 'ph-duotone ph-check-circle'" class="text-sm"></i>
                {{ file.resolved ? '标记未解决' : '标记已解决' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="files.length === 0" class="text-center py-8 text-slate-400">
        暂无文件数据
      </div>
      
      <!-- 分页 -->
      <div v-if="!loading && totalItems > 0" class="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
        <div class="text-sm text-slate-400">
          共 <span class="text-white font-medium">{{ totalItems }}</span> 条记录，第 <span class="text-white font-medium">{{ page }}</span> / <span class="text-white font-medium">{{ totalPages }}</span> 页
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="$emit('page-change', page - 1)"
            :disabled="page <= 1"
            class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            :class="page <= 1 ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
          >
            <i class="ph-bold ph-caret-left"></i>
          </button>
          <div class="flex items-center gap-1">
            <template v-for="p in visiblePages" :key="p">
              <button
                v-if="p !== '...'"
                @click="$emit('page-change', p)"
                class="min-w-[32px] px-2 py-1.5 text-sm rounded-lg border transition-all"
                :class="p === page 
                  ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' 
                  : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
              >
                {{ p }}
              </button>
              <span v-else class="px-2 text-slate-500">...</span>
            </template>
          </div>
          <button
            @click="$emit('page-change', page + 1)"
            :disabled="page >= totalPages"
            class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            :class="page >= totalPages ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
          >
            <i class="ph-bold ph-caret-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  files: { type: Array, default: () => [] },
  filterResolved: { type: [Boolean, Number], default: null },
  loading: { type: Boolean, default: false },
  formatFileSize: { type: Function, required: true },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  total: { type: Number, default: 0 }
});

const emit = defineEmits(['update:filter-resolved', 'load', 'mark-resolved', 'page-change', 'toggle-resolved']);

const filterOptions = [
  { value: null, label: '全部' },
  { value: false, label: '未解决' },
  { value: true, label: '已解决' }
];

const filterDropdownOpen = ref(false);
const filterResolvedLabel = computed(() => {
  const hit = filterOptions.find(o => o.value === props.filterResolved);
  return hit ? hit.label : '全部';
});

const selectFilter = (val) => {
  filterDropdownOpen.value = false;
  emit('update:filter-resolved', val);
  emit('load');
};

const totalItems = computed(() => props.total || props.files.length);

const totalPages = computed(() => {
  return Math.ceil(totalItems.value / props.pageSize) || 1;
});

const pagedFiles = computed(() => {
  const start = (props.page - 1) * props.pageSize;
  const end = start + props.pageSize;
  return props.files.slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const current = props.page;
  const total = totalPages.value;
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    }
  }
  
  return pages;
});
</script>

